import parse, { HTMLReactParserOptions, domToReact } from 'html-react-parser'
import { Element } from 'domhandler'
import { Link as RouterLink } from 'react-router-dom'
import {
  chakra,
  Box,
  BoxProps,
  forwardRef,
  Text,
  Link as ChackraLink,
  UnorderedList as ChakraUnorderedList,
  OrderedList as ChakraOrderedList,
  ListItem as ChackraListItem,
} from '@chakra-ui/react'

import { checkExternalLink, formatTicketDate } from '../../lib'
import { ITicketComment } from '../../api'

export interface ITicketBoxProps extends BoxProps {
  comment: ITicketComment
}

const UnorderedList = chakra(ChakraUnorderedList, {
  baseStyle: {
    mt: 4,
    pl: 4,
  },
})

const OrderedList = chakra(ChakraOrderedList, {
  baseStyle: {
    mt: 4,
    pl: 4,
  },
})

const ListItem = chakra(ChackraListItem, {})

const Link = chakra(ChackraLink, {
  baseStyle: {
    color: 'coral.500',
  },
})

const isElement = (node: any): node is Element => {
  return node instanceof Element || !!(node as Element).attribs
}

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (isElement(domNode) && domNode.attribs) {
      switch (domNode.tagName) {
        case 'p': {
          if (!domNode.children.length) {
            return <></>
          }
          return <Text>{domToReact(domNode.children, options)}</Text>
        }
        case 'ul': {
          return <UnorderedList>{domToReact(domNode.children, options)}</UnorderedList>
        }
        case 'ol': {
          return <OrderedList>{domToReact(domNode.children, options)}</OrderedList>
        }
        case 'li': {
          return <ListItem>{domToReact(domNode.children, options)}</ListItem>
        }
        case 'a': {
          const { href, ...rest } = domNode.attribs
          if (!href) {
            return null
          }
          const { url, isPageLink } = checkExternalLink(href)
          if (isPageLink) {
            return (
              <Link as={RouterLink} to={url} {...rest}>
                {domToReact(domNode.children, options)}
              </Link>
            )
          }
          return (
            <Link href={url} {...rest}>
              {domToReact(domNode.children, options)}
            </Link>
          )
        }
      }
    }
  },
}

export const TicketCommentBox = forwardRef<ITicketBoxProps, 'div'>((props, ref) => {
  const { comment, ...rest } = props
  const htmlContent = parse(comment.htmlBody, options)
  return (
    <Box ref={ref} borderWidth={1} p={[3, 4]} borderRadius="sm" {...rest}>
      <Text fontWeight="bold">
        {comment.author}{' '}
        <Text as="span" fontWeight="normal" color="gray.600">
          {formatTicketDate(comment.createdAt)}
        </Text>
      </Text>
      <Box mt={4}>{htmlContent}</Box>
    </Box>
  )
})
